<template>
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <AppCard>
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-xl font-bold">Collected</h1>
        <Select
          v-model="dateRange"
          :options="DATE_RANGE_OPTIONS"
          option-label="label"
          option-value="value"
          class="w-48"
          @change="handleDateRangeChange"
        />
      </div>

      <div
        v-if="error"
        class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg"
      >
        <div class="text-red-800 text-sm">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          {{ error }}
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Total Revenue
          </div>
          <div class="font-semibold">
            <span v-if="isLoading || statisticsData.total_revenue == null"
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.total_revenue"
              :format="{ style: 'currency', currency: 'USD' }"
              class="~text-2xl/4xl"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Total Customers
          </div>
          <div class="font-semibold">
            <span v-if="isLoading || statisticsData.total_customers == null"
              >—</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.total_customers"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Avg. Payment Value
          </div>
          <div class="font-semibold">
            <span
              v-if="isLoading || statisticsData.average_payment_value == null"
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.average_payment_value"
              :format="{ style: 'currency', currency: 'USD' }"
              class="~text-2xl/4xl"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Total Payments
          </div>
          <div class="font-semibold">
            <span
              v-if="isLoading || statisticsData.total_payments_count == null"
              >—</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.total_payments_count"
            />
          </div>
        </div>
      </div>
    </AppCard>
    <AppCard>
      <h1 class="text-xl font-bold mb-4">Outstanding</h1>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div
            class="text-surface-500 dark:text-surface-300 text-sm flex items-center gap-1"
          >
            Total Outstanding
            <i
              v-tooltip.top="
                'Total amount expected to collect from all active payment plans, including future installments'
              "
              class="pi pi-info-circle text-xs cursor-help"
            ></i>
          </div>
          <div class="font-semibold">
            <span v-if="isLoading || statisticsData.outstanding_total == null"
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.outstanding_total"
              :format="{ style: 'currency', currency: 'USD' }"
              class="~text-2xl/4xl"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Invoices
          </div>
          <div class="font-semibold text-lg">
            <span
              v-if="isLoading || statisticsData.outstanding_invoices == null"
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.outstanding_invoices"
              :format="{ style: 'currency', currency: 'USD' }"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Registrations
          </div>
          <div class="font-semibold text-lg">
            <span
              v-if="
                isLoading || statisticsData.outstanding_registrations == null
              "
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.outstanding_registrations"
              :format="{ style: 'currency', currency: 'USD' }"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div
            class="text-surface-500 dark:text-surface-300 text-sm flex items-center gap-1"
          >
            Memberships
            <i
              v-tooltip.top="
                'Next scheduled payment for active memberships only'
              "
              class="pi pi-info-circle text-xs cursor-help"
            ></i>
          </div>
          <div class="font-semibold text-lg">
            <span
              v-if="isLoading || statisticsData.outstanding_memberships == null"
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.outstanding_memberships"
              :format="{ style: 'currency', currency: 'USD' }"
            />
          </div>
        </div>
      </div>
    </AppCard>
    <AppCard>
      <h1 class="text-xl font-bold mb-4">Overdue</h1>
      <div class="grid grid-cols-1 gap-4 mb-4">
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Past Due Revenue
          </div>
          <div class="font-semibold">
            <span v-if="isLoading || statisticsData.past_due_revenue == null"
              >$ —</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.past_due_revenue"
              :format="{ style: 'currency', currency: 'USD' }"
              class="~text-2xl/4xl"
            />
          </div>
        </div>
        <div class="p-4 rounded-lg shadow-md border-surface-200 border-[1px]">
          <div class="text-surface-500 dark:text-surface-300 text-sm">
            Past Due Payments
          </div>
          <div class="font-semibold">
            <span
              v-if="isLoading || statisticsData.past_due_payments_count == null"
              >—</span
            >
            <NumberFlow
              v-else
              will-change
              :plugins="[continuous]"
              :value="statisticsData.past_due_payments_count"
            />
          </div>
        </div>
      </div>
    </AppCard>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import AppCard from "@/shared/components/AppCard.vue";
import Select from "primevue/select";
import { usePaymentsLogPaypointStatistics } from "../composables/usePaymentsLogPaypointStatistics";
import NumberFlow, { continuous } from "@number-flow/vue";
import { DATE_RANGE_OPTIONS } from "../constants";
import { getDateRangeParams } from "../helpers";

const { fetchPaymentsLogStatistics, statisticsData, isLoading, error } =
  usePaymentsLogPaypointStatistics();

const dateRange = ref("last-30-days");

const handleDateRangeChange = async () => {
  const params = getDateRangeParams(dateRange.value);
  await fetchPaymentsLogStatistics(params);
};

const refreshStatistics = async () => {
  const params = getDateRangeParams(dateRange.value);
  await fetchPaymentsLogStatistics(params);
};

defineExpose({
  refreshStatistics,
});

onMounted(async () => {
  const params = getDateRangeParams(dateRange.value);
  await fetchPaymentsLogStatistics(params);
});
</script>
